import { Extension, Editor, CommandProps } from '@tiptap/core';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { SelectChangeEvent } from '@mui/material/Select';
import { ReactElement } from 'react';

interface ParagraphStyleExtensionOptions {
  types: string[];
  styles: Array<{ value: string; label: string }>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    paragraphStyle: {
      setParagraphStyle: (className: string) => ReturnType;
      removeParagraphStyle: () => ReturnType;
    };
  }
}

const ParagraphStyleExtension = Extension.create<ParagraphStyleExtensionOptions>({
  name: 'paragraphStyle',
  addOptions() {
    return {
      types: ['paragraph'],
      styles: [],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          class: {
            default: null,
            parseHTML: element => element.getAttribute('class'),
            renderHTML: attributes => {
              if (!attributes.class) {
                return {};
              }
              return { class: attributes.class };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setParagraphStyle:
        (className: string) =>
        ({ chain }: CommandProps) => {
          return chain()
            .updateAttributes('paragraph', { class: className })
            .run();
        },
      removeParagraphStyle:
        () =>
        ({ chain }: CommandProps) => {
          return chain()
            .updateAttributes('paragraph', { class: null })
            .run();
        },
    };
  },
});

interface ParagraphStyleControlProps {
  editor: Editor | null | undefined;
  styles: Array<{ value: string; label: string }>;
}

const ParagraphStyleControl = ({
  editor,
  styles,
}: ParagraphStyleControlProps): ReactElement | null => {
  if (!editor) {
    return null;
  }

  const currentClass = editor.getAttributes('paragraph').class || '';

  const handleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    if (!editor) return;

    if (!value) {
      editor.commands.removeParagraphStyle();
      return;
    }
    editor.commands.setParagraphStyle(value);
  };

  return (
<Select
  value={currentClass}
  onChange={handleChange}
  size="small"
  sx={{
    minWidth: 120,
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none', // Remove default border
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: '1px solid rgba(0, 0, 0, 0.23)', // Show border on hover
    },
    '& .MuiSelect-select': {
      padding: '4px 14px', // Reduce padding (default is 8px 14px for size="small")
    }
  }}
  displayEmpty
  renderValue={(value) => {
    if (!value) {
      return <em>paragraph style</em>;
    }
    return value;
  }}
>
  <MenuItem value="">
    <em>None</em>
  </MenuItem>
  {styles.map(style => (
    <MenuItem key={style.value} value={style.value}>
      {style.label}
    </MenuItem>
  ))}
</Select>
  );
};

export type { ParagraphStyleExtensionOptions };
export { ParagraphStyleExtension, ParagraphStyleControl };